import React from 'react'

import { ModalInjectedProps, ConfirmationDialog } from '@sketch/components'
import {
  usePublishShareMutation,
  GetProfilePublicationsDocument,
  GetProfilePublicationsQueryVariables,
} from '@sketch/gql-types'
import { useToast } from '@sketch/toasts'

interface PublishDocumentNowModalProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  workspaceIdentifier: string
  publicCommentsEnabled?: boolean
  publicInspectEnabled?: boolean
}

/**
 * This modal only publishes the document without any extra logic as opposed to "PublishDocumentModal"
 * which has much more logic around publishing a document if the profile already exists or not.
 *
 * To avoid adding even more logic to the "PublishDocumentModal", this modal is a simpler version
 * that just handles the publishing of the document.
 */
export const PublishDocumentNowModal = ({
  hideModal,
  shareName,
  shareIdentifier,
  workspaceIdentifier,
  publicCommentsEnabled = false,
  publicInspectEnabled = false,
}: PublishDocumentNowModalProps) => {
  const { showToast } = useToast()

  const [publishShare, { loading: isPublishing }] = usePublishShareMutation({
    onError: 'show-toast',
    onCompleted: () => {
      showToast(`"${shareName}" successfully published`)
      hideModal()
    },
    refetchQueries: [
      {
        query: GetProfilePublicationsDocument,
        variables: {
          workspaceIdentifier,
        } as GetProfilePublicationsQueryVariables,
      },
    ],
    awaitRefetchQueries: true,
  })

  const handleConfirm = () => {
    publishShare({
      variables: {
        shareIdentifier,
        publicCommentsEnabled,
        publicInspectEnabled,
      },
    })
  }

  return (
    <ConfirmationDialog
      hideModal={hideModal}
      title={'Publish Document?'}
      onConfirm={handleConfirm}
      confirmButton={{
        text: 'Publish',
        variant: 'primary',
        loading: isPublishing,
      }}
      cancelButton={{
        text: 'Cancel',
        disabled: isPublishing,
      }}
    >
      <p>
        After you publish &ldquo;<strong>{shareName}</strong>&rdquo;, everybody
        will be able to view and download it from the Workspace profile.
      </p>
      <p>
        Only the last saved or starred version will be displayed. Updates are
        automatically published.
      </p>
    </ConfirmationDialog>
  )
}
